/**
 * LivretTopolinoAM.jsx — Livret de suivi interactif Permis AM Voiturette (Fiat Topolino).
 *
 * Fonctionnalités :
 *  - Checkboxes 4 états (à découvrir / en cours / acquis / à retravailler)
 *  - Champs texte libre pour questions ouvertes + notes enseignant + signatures
 *  - Suivi de l'autonomie kilométrique en inputs numériques
 *  - Persistance localStorage + sync cloud via useLivretSync (si connecté)
 *  - Progression globale par section + globale (badge %)
 *  - Bouton "Projecteur" pour projection en salle
 *  - Bouton "Imprimer" avec mise en page adaptée
 *  - Deep-link `?etape=id` pour ouvrir directement une étape
 *
 * Route : /livrets/topolino-am
 */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Home, Printer, Save, User, GraduationCap, Calendar,
  CheckCircle2, ChevronDown, ChevronRight, Sparkles, Gauge, Route,
  Battery, Award, Signature, Lock,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import useSeo from '../../hooks/useSeo';
import useLivretSync from '../../hooks/useLivretSync';
import { LIVRET_TOPOLINO, getAllCheckItemIds, CHECK_STATES } from '../../data/livrets/livretTopolinoAM';
import { CheckStatus } from '../../components/livret/CheckStatus';
import { ProjectorToggle } from '../../components/ProjectorToggle';
import { toast } from 'sonner';
import { celebrateUnlock } from '../../utils/celebrateUnlock';

const ALL_IDS = getAllCheckItemIds();

export default function LivretTopolinoAM() {
  const [params, setParams] = useSearchParams();
  const { state, save, saveProfil, loading, syncing } = useLivretSync(LIVRET_TOPOLINO.key);
  const profil = state.__profil || {};

  // Ouverture initiale : deep-link ?etape=id → on ouvre la section correspondante
  const activeEtapeSlug = params.get('etape');
  const [openSections, setOpenSections] = useState(() => {
    const initial = {};
    LIVRET_TOPOLINO.sections.forEach((s, i) => {
      // Toutes ouvertes par défaut sur desktop, seule la 1ère sur mobile — on ouvre tout pour l'usage prof
      initial[s.id] = true;
    });
    return initial;
  });

  useSeo({
    title: 'Livret de suivi Topolino — Permis AM Voiturette · Code 2 Roues',
    description: 'Livret pédagogique interactif pour la formation Permis AM option quadricycle léger (Fiat Topolino) — mercredi maîtrise & samedi circulation.',
  });

  // Progression globale
  const stats = useMemo(() => {
    const total = ALL_IDS.length;
    let ok = 0, wip = 0, rt = 0;
    ALL_IDS.forEach((id) => {
      const v = state[id];
      if (v === 'ok') ok++;
      else if (v === 'wip') wip++;
      else if (v === 'retravailler') rt++;
    });
    return { total, ok, wip, rt, pct: total > 0 ? Math.round((ok / total) * 100) : 0 };
  }, [state]);

  // Progression par section
  const sectionStats = useMemo(() => {
    const m = {};
    LIVRET_TOPOLINO.sections.forEach((s) => {
      const ids = [];
      s.etapes.forEach((e) => {
        (e.groups || []).forEach((g) => (g.items || []).forEach((it) => ids.push(it.id)));
        if (e.critere) ids.push(e.critere.id);
        if (e.exercice) ids.push(e.exercice.id);
      });
      const total = ids.length;
      const ok = ids.filter((id) => state[id] === 'ok').length;
      m[s.id] = { total, ok, pct: total > 0 ? Math.round((ok / total) * 100) : 0 };
    });
    return m;
  }, [state]);

  /* ── Progression linéaire des sections (Sn+1 débloquée quand Sn est 100 % ok) ──
     La 1re section (mercredi / hors circulation) est toujours accessible.
     La 2e section (samedi / circulation) se débloque quand mercredi est complet. */
  const sectionUnlocked = useMemo(() => {
    const map = {};
    let previousComplete = true;
    LIVRET_TOPOLINO.sections.forEach((s) => {
      const st = sectionStats[s.id];
      const complete = st && st.total > 0 && st.ok === st.total;
      map[s.id] = { unlocked: previousComplete, complete };
      previousComplete = complete;
    });
    return map;
  }, [sectionStats]);

  /* ── Célébration au déblocage d'une section ── */
  const prevSectionStatusRef = useRef(null);
  useEffect(() => {
    const prev = prevSectionStatusRef.current;
    prevSectionStatusRef.current = sectionUnlocked;
    if (!prev) return;
    LIVRET_TOPOLINO.sections.forEach((s, idx) => {
      const wasComplete = prev[s.id]?.complete;
      const nowComplete = sectionUnlocked[s.id]?.complete;
      if (!wasComplete && nowComplete) {
        const nextSec = LIVRET_TOPOLINO.sections[idx + 1];
        if (nextSec) {
          celebrateUnlock({
            title: `🎉 ${s.title} — validée !`,
            message: `Tu débloques : ${nextSec.title}`,
            color: nextSec.color,
            accentColors: [s.color, nextSec.color, '#fbbf24', '#facc15', '#ffffff'],
          });
          // Auto-ouvrir la section nouvellement débloquée
          setOpenSections((o) => ({ ...o, [nextSec.id]: true }));
        } else {
          celebrateUnlock({
            title: '🏁 Livret Topolino complet !',
            message: 'Toutes les étapes sont acquises — bravo pour ton parcours.',
            color: s.color,
            durationMs: 4200,
            accentColors: ['#fbbf24', '#facc15', '#10b981', '#22d3ee', '#f43f5e', '#ffffff'],
          });
        }
      }
    });
  }, [sectionUnlocked]);

  const setItem = (id, v) => save({ [id]: v });
  const setProfil = (partial) => saveProfil({ ...profil, ...partial });

  return (
    <div className="min-h-screen bg-background text-foreground pb-16" data-testid="livret-topolino-am">
      {/* Sticky topbar */}
      <div className="sticky top-0 z-40 border-b border-white/10 bg-background/95 backdrop-blur print:hidden">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 flex items-center gap-2 sm:gap-3">
          <Button asChild variant="outline" size="sm">
            <Link to="/permis-am/voiturette" aria-label="Retour au Hub Voiturette">
              <ArrowLeft className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Hub Voiturette</span>
            </Link>
          </Button>
          <div className="flex-1 text-center min-w-0">
            <div className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.22em] text-rose-500 truncate whitespace-nowrap">
              Livret Topolino · Permis AM
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.print()} className="hidden sm:inline-flex" data-testid="btn-print">
            <Printer className="w-4 h-4 mr-1" /> Imprimer
          </Button>
          <ProjectorToggle testid="proj-livret-topolino" />
          <Button asChild variant="ghost" size="sm">
            <Link to="/" aria-label="Retour à l'accueil">
              <Home className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Accueil</span>
            </Link>
          </Button>
        </div>
      </div>

      <article className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        {/* Hero */}
        <header className="mb-8 lg:mb-12 text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-[10px] font-black uppercase tracking-widest text-rose-400 mb-4">
            <Award className="w-3 h-3" /> Livret pédagogique interactif
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {LIVRET_TOPOLINO.meta.title}
          </h1>
          <p className="text-base lg:text-lg text-rose-500 font-black tracking-wide">
            {LIVRET_TOPOLINO.meta.subtitle}
          </p>
          <p className="mt-2 text-white/60 italic">{LIVRET_TOPOLINO.meta.tagline}</p>

          {/* Sync indicator */}
          {syncing && (
            <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-emerald-400 font-black">
              <Save className="w-3 h-3 animate-pulse" /> Sauvegarde en cours…
            </div>
          )}
          {loading && <div className="mt-3 text-[11px] text-white/50">Chargement…</div>}
        </header>

        {/* Bloc profil */}
        <section className="mb-8 p-5 rounded-2xl border-2 border-white/10 bg-white/[0.02] print:border-black print:bg-white print:text-black" data-testid="livret-profil">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-rose-400 mb-3 print:text-black">Identification</div>
          <div className="grid gap-3 sm:grid-cols-3">
            <ProfilField icon={User} label="Élève" value={profil.eleve} onChange={(v) => setProfil({ eleve: v })} testid="profil-eleve" />
            <ProfilField icon={GraduationCap} label="Enseignant" value={profil.enseignant} onChange={(v) => setProfil({ enseignant: v })} testid="profil-enseignant" />
            <ProfilField icon={Calendar} label="Date" type="date" value={profil.date} onChange={(v) => setProfil({ date: v })} testid="profil-date" />
          </div>
        </section>

        {/* Progression globale */}
        <section className="mb-8 p-5 rounded-2xl border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-600/10 via-emerald-500/5 to-transparent" data-testid="livret-progress">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-400 mb-1">Progression globale</div>
              <div className="text-3xl sm:text-4xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {stats.pct}<span className="text-emerald-400">%</span>
                <span className="text-base sm:text-lg text-white/70 font-bold ml-2">acquis</span>
              </div>
              <div className="mt-1 text-[12px] text-white/60">
                {stats.ok} <span className="text-emerald-400">acquis</span> · {stats.wip} <span className="text-amber-400">en cours</span> · {stats.rt} <span className="text-rose-400">à retravailler</span> · sur {stats.total} items
              </div>
            </div>
            {/* Barre */}
            <div className="w-full sm:w-64">
              <div className="h-3 rounded-full bg-black/40 border border-white/10 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${stats.pct}%`, background: 'linear-gradient(90deg, #10b981, #22c55e)' }} />
              </div>
              <div className="mt-2 grid grid-cols-4 gap-1">
                {CHECK_STATES.map((s) => (
                  <div key={s.key} className="flex items-center gap-1 text-[10px] text-white/70 print:text-black">
                    <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                    <span className="truncate">{s.short}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Sections — progression pas à pas : samedi verrouillée tant que mercredi n'est pas 100 % OK */}
        <div className="space-y-6">
          {LIVRET_TOPOLINO.sections.map((section) => {
            const st = sectionUnlocked[section.id] || { unlocked: true, complete: false };
            return (
              <SectionCard
                key={section.id}
                section={section}
                stats={sectionStats[section.id]}
                open={st.unlocked && openSections[section.id]}
                locked={!st.unlocked}
                complete={st.complete}
                onToggle={() => {
                  if (!st.unlocked) {
                    toast.info("Termine d'abord la section précédente (100 % acquis) pour débloquer celle-ci.");
                    return;
                  }
                  setOpenSections((o) => ({ ...o, [section.id]: !o[section.id] }));
                }}
                state={state}
                setItem={setItem}
                save={save}
              />
            );
          })}
        </div>

        {/* Bandeau pédagogique progression linéaire */}
        <div className="mt-3 mb-6 print:hidden">
          <div className="flex items-center gap-2 text-[11.5px] text-white/60 leading-snug">
            <Lock className="w-3.5 h-3.5 text-rose-400/70 flex-shrink-0" />
            <span>
              <strong className="text-white/85">Progression pas à pas :</strong>{' '}
              la section suivante se débloque quand tous les items de la précédente sont marqués <em className="text-emerald-400 not-italic font-black">Acquis</em>.
            </span>
          </div>
        </div>

        {/* 5 réflexes */}
        <section className="mt-10 p-6 rounded-2xl border-2 border-rose-500/30 bg-gradient-to-br from-rose-600/10 via-rose-500/5 to-transparent" data-testid="livret-reflexes">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-rose-400 mb-1">🎯 À retenir toujours</div>
          <h2 className="text-2xl font-black mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Les 5 réflexes du conducteur AM</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {LIVRET_TOPOLINO.reflexes.map((r, i) => (
              <div key={i} className="p-3 rounded-xl bg-black/30 border border-white/10 text-center">
                <div className="text-2xl mb-1">{r.emoji}</div>
                <div className="text-[11px] font-black uppercase tracking-widest text-rose-400 mb-1">{i + 1}. {r.title}</div>
                <div className="text-[12px] text-white/75">{r.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Signatures */}
        <section className="mt-8 grid gap-4 sm:grid-cols-2 print:break-inside-avoid">
          <SignatureBlock label="Signature élève" value={profil.sign_eleve} onChange={(v) => setProfil({ sign_eleve: v })} testid="sign-eleve" />
          <SignatureBlock label="Signature enseignant" value={profil.sign_enseignant} onChange={(v) => setProfil({ sign_enseignant: v })} testid="sign-enseignant" />
        </section>
      </article>
    </div>
  );
}

/* ────────────────── Composants internes ────────────────── */

function ProfilField({ icon: Icon, label, value, onChange, type = 'text', testid }) {
  return (
    <label className="block">
      <div className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-white/60 mb-1 print:text-black">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <Input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={label}
        data-testid={testid}
        className="bg-black/30 border-white/15 text-white print:bg-white print:text-black print:border-black"
      />
    </label>
  );
}

function SectionCard({ section, stats, open, onToggle, state, setItem, save, locked = false, complete = false }) {
  return (
    <section
      id={section.id}
      className={`rounded-2xl border-2 overflow-hidden transition ${locked ? 'opacity-90' : ''}`}
      style={{ borderColor: locked ? 'rgba(244,63,94,0.35)' : `${section.color}66` }}
      data-testid={`section-${section.id}`}
      data-locked={locked ? 'true' : 'false'}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-disabled={locked}
        className={`w-full text-left px-5 py-4 flex flex-wrap items-center gap-3 justify-between transition print:hidden ${
          locked ? 'cursor-not-allowed' : 'hover:bg-white/[0.02]'
        }`}
        aria-expanded={open}
        style={{ background: locked ? 'rgba(244,63,94,0.06)' : `${section.color}18` }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-black shrink-0 ${locked ? 'opacity-60' : ''}`}
            style={{ background: locked ? '#475569' : section.color }}
          >
            {locked ? <Lock className="w-4 h-4" /> : section.day.charAt(0)}
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] flex items-center gap-1.5" style={{ color: locked ? '#fda4af' : section.color }}>
              {locked && <span>Verrouillée · </span>}
              {section.kicker}
            </div>
            <div
              className={`text-lg font-black leading-tight truncate ${locked ? 'text-white/60' : 'text-white'}`}
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {section.title}
            </div>
            {section.subtitle && <div className="text-[12px] text-white/60 truncate">{section.subtitle}</div>}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {stats && !locked && (
            <div className="text-right">
              <div className="text-[10px] font-black uppercase tracking-widest text-white/60">{stats.ok} / {stats.total}</div>
              <div className="text-lg font-black text-white">{stats.pct}%</div>
            </div>
          )}
          {complete && !locked && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[9.5px] font-black uppercase tracking-widest"
              title="Section 100 % acquise"
            >
              <CheckCircle2 className="w-3 h-3" /> Terminée
            </span>
          )}
          {locked ? (
            <Lock className="w-5 h-5 text-rose-400/70" />
          ) : open ? (
            <ChevronDown className="w-5 h-5 text-white/60" />
          ) : (
            <ChevronRight className="w-5 h-5 text-white/60" />
          )}
        </div>
      </button>

      {/* Placeholder si verrouillé (visible seulement en preview écran, pas à l'impression) */}
      {locked && (
        <div className="px-5 py-5 bg-black/20 border-t border-rose-500/20 print:hidden">
          <div className="flex items-start gap-3 text-[13px] text-white/70 leading-relaxed">
            <Lock className="w-4 h-4 text-rose-400/70 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-white/85 font-black mb-1">Cette section est verrouillée</div>
              <div>
                Termine d'abord la section précédente à <strong className="text-white">100 % acquis</strong>{' '}
                pour débloquer <strong className="text-white">{section.title}</strong>. Une célébration
                confettis viendra saluer chaque déblocage 🎉
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version imprimable — toujours ouverte + titre visible */}
      <div className="hidden print:block px-5 pt-4">
        <div className="text-black font-black uppercase tracking-widest text-sm">{section.kicker} — {section.title}</div>
      </div>

      {!locked && (open || true) && (
        <div className={`${open ? 'block' : 'hidden'} print:block px-5 py-5 space-y-6`}>
          {section.etapes.map((etape) => (
            <EtapeBlock
              key={etape.id}
              etape={etape}
              color={section.color}
              state={state}
              setItem={setItem}
              save={save}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function EtapeBlock({ etape, color, state, setItem, save }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-4 print:border-black print:bg-white" data-testid={`etape-${etape.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white font-black text-[13px] shrink-0" style={{ background: `${color}30`, color }}>
          {etape.num}
        </div>
        <div className="flex-1">
          <h3 className="text-base sm:text-lg font-black text-white leading-tight print:text-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Étape {etape.num} — {etape.title}
          </h3>
          {etape.hint && <p className="text-[12.5px] text-white/60 italic mt-0.5 print:text-black">{etape.hint}</p>}
        </div>
      </div>

      {/* Groupes de check-items */}
      {(etape.groups || []).map((group, gi) => (
        <div key={gi} className={gi > 0 ? 'mt-4 pt-4 border-t border-white/10 print:border-black' : ''}>
          {group.title && (
            <div className="text-[11px] font-black uppercase tracking-widest text-white/60 mb-2 print:text-black">{group.title}</div>
          )}
          <ul className="space-y-1.5">
            {(group.items || []).map((it) => (
              <CheckLine key={it.id} id={it.id} label={it.label} value={state[it.id]} onChange={(v) => setItem(it.id, v)} />
            ))}
          </ul>
        </div>
      ))}

      {/* Critère */}
      {etape.critere && (
        <div className="mt-3 pt-3 border-t border-emerald-500/30 print:border-black">
          <div className="text-[11px] font-black uppercase tracking-widest text-emerald-400 mb-1.5 print:text-black">✓ Critère</div>
          <CheckLine id={etape.critere.id} label={etape.critere.label} value={state[etape.critere.id]} onChange={(v) => setItem(etape.critere.id, v)} tone="emerald" />
        </div>
      )}

      {/* Exercice */}
      {etape.exercice && (
        <div className="mt-3 pt-3 border-t border-sky-500/30 print:border-black">
          <div className="text-[11px] font-black uppercase tracking-widest text-sky-400 mb-1.5 print:text-black">🎯 Exercice</div>
          <CheckLine id={etape.exercice.id} label={etape.exercice.label} value={state[etape.exercice.id]} onChange={(v) => setItem(etape.exercice.id, v)} tone="sky" />
        </div>
      )}

      {/* Questions ouvertes */}
      {(etape.questions || []).map((q) => (
        <div key={q.id} className="mt-4">
          <label className="block">
            <div className="text-[11px] font-black uppercase tracking-widest text-amber-400 mb-1.5 print:text-black">💬 Question</div>
            <div className="text-[13.5px] text-white/85 mb-2 italic print:text-black">{q.label}</div>
            <Textarea
              value={state[q.id] || ''}
              onChange={(e) => save({ [q.id]: e.target.value })}
              rows={2}
              placeholder="Réponse de l'élève / note de l'enseignant…"
              className="bg-black/30 border-white/15 text-white print:bg-white print:text-black print:border-black"
              data-testid={`textarea-${q.id}`}
            />
          </label>
        </div>
      ))}

      {/* Suivi kilométrique */}
      {(etape.km || []).length > 0 && (
        <div className="mt-4 pt-3 border-t border-violet-500/30 print:border-black">
          <div className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-violet-400 mb-2 print:text-black">
            <Battery className="w-3.5 h-3.5" /> Suivi kilométrique / autonomie
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {etape.km.map((k) => (
              <label key={k.id} className="flex items-center gap-2">
                <span className="flex-1 text-[13px] text-white/85 print:text-black">{k.label}</span>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={state[k.id] || ''}
                  onChange={(e) => save({ [k.id]: e.target.value })}
                  className="w-24 bg-black/30 border-white/15 text-white text-right print:bg-white print:text-black print:border-black"
                  placeholder="—"
                  data-testid={`km-${k.id}`}
                />
                <span className="text-[11px] text-white/60 w-8 print:text-black">{k.unit}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CheckLine({ id, label, value, onChange, tone }) {
  return (
    <li className="flex items-start gap-3 group print:break-inside-avoid" data-testid={`check-line-${id}`}>
      <div className="mt-0.5">
        <CheckStatus value={value} onChange={onChange} testid={`check-btn-${id}`} size="sm" />
      </div>
      <span className={`flex-1 text-[13.5px] leading-relaxed pt-1 print:text-black ${value === 'ok' ? 'text-emerald-100' : value === 'retravailler' ? 'text-rose-100' : value === 'wip' ? 'text-amber-100' : 'text-white/85'}`}>
        {label}
      </span>
    </li>
  );
}

function SignatureBlock({ label, value, onChange, testid }) {
  return (
    <div className="p-4 rounded-xl border-2 border-white/10 bg-black/20 print:bg-white print:border-black" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-white/60 mb-2 print:text-black">
        <Signature className="w-3.5 h-3.5" /> {label}
      </div>
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Nom + date"
        className="bg-black/30 border-white/15 text-white print:bg-white print:text-black print:border-black"
      />
      <div className="mt-3 h-16 rounded border border-dashed border-white/20 print:border-black" />
    </div>
  );
}
